<!-- 1. HIGH-IMPACT CYBER NEURAL LOADER -->
<div class="ai-loader-overlay" *ngIf="isLoading">
    <div class="loader-content">
      <div class="cyber-spinner">
        <div class="outer-ring"></div>
        <div class="inner-ring"></div>
        <div class="core-glow"></div>
        <div class="neural-nodes">
          <span class="node n1"></span>
          <span class="node n2"></span>
          <span class="node n3"></span>
        </div>
      </div>
      <div class="loader-text-group">
        <h3 class="loader-title">AI DIAGNOSTIC ENGINE ACTIVE</h3>
        <p class="loader-subtitle">Evaluating Telemetry Streams • Cross-Referencing IEC 60599, IEC 60422 & IEEE C57.104</p>
      </div>
      <div class="loader-progress-bar">
        <div class="loader-progress-fill"></div>
      </div>
    </div>
  </div>
  
  <!-- 2. ERROR STATE CONTAINER -->
  <div class="error-container glass-card" *ngIf="!isLoading && hasError">
    <div class="error-content">
      <div class="error-badge">SYSTEM DIAGNOSTIC FAULT</div>
      <h3>Telemetry Analysis Interrupted</h3>
      <p>{{ errorMessage }}</p>
      <button class="btn-cyber-primary" (click)="fetchAssessmentData()">
        <span class="btn-icon">⚡</span> RE-INITIALIZE DIAGNOSTICS
      </button>
    </div>
  </div>
  
  <!-- 3. MAIN INDUSTRIAL DIAGNOSTIC DASHBOARD -->
  <div class="dashboard-wrapper" *ngIf="!isLoading && !hasError && data">
  
    <!-- TOP ENGINE STATUS & AI CONFIDENCE HEADER -->
    <div class="top-status-bar glass-card">
      <div class="engine-origin-pills">
        <div class="origin-tag iec-tag">
          <span class="tag-status-dot blue-dot"></span>
          <span class="tag-label">DETERMINISTIC ENGINE:</span>
          <span class="tag-val">IEC 60599 / IEC 60422 / IEEE C57.104</span>
        </div>
        <div class="origin-tag ai-tag">
          <span class="tag-status-dot purple-dot"></span>
          <span class="tag-label">PREDICTIVE MODEL:</span>
          <span class="tag-val">Transformer Health Net v2.4</span>
        </div>
      </div>
  
      <!-- AI CONFIDENCE METRIC -->
      <div class="confidence-gauge-box" *ngIf="data.remainingUsefulLife?.confidence || data.aiAssessment?.confidence">
        <div class="gauge-meta">
          <span class="gauge-label">AI PREDICTION CONFIDENCE</span>
          <span class="gauge-score">{{ data.aiAssessment?.confidence || data.remainingUsefulLife?.confidence || 94.8 }}%</span>
        </div>
        <div class="gauge-track">
          <div class="gauge-fill" [style.width.%]="data.aiAssessment?.confidence || data.remainingUsefulLife?.confidence || 94.8"></div>
        </div>
      </div>
    </div>
  
    <!-- HERO ASSET DIAGNOSTIC BANNER -->
    <header class="hero-asset-card glass-card" [style.border-left-color]="getColorHex(data.summary?.color)">
      <div class="hero-left">
        <div class="asset-pills">
          <span class="asset-type-badge">{{ data.asset?.type || 'Power Transformer' }} ({{ data.asset?.coolingMethod }})</span>
          <span class="source-pill ai-pill">AI Executive Synthesis</span>
        </div>
        <h1 class="asset-id">{{ data.asset?.transformerId || 'Asset Telemetry Analysis' }}</h1>
        <p class="asset-desc">{{ data.executiveSummary?.message || data.summary?.message }}</p>
      </div>
  
      <!-- DYNAMIC GLOW METRIC CARDS -->
      <div class="hero-kpis">
        <!-- HEALTH INDEX -->
        <div class="kpi-box" *ngIf="data.healthAssessment" [style.border-bottom-color]="getColorHex(data.healthAssessment.color)">
          <div class="kpi-top">
            <span class="kpi-label">HEALTH INDEX</span>
            <span class="origin-badge hybrid-bg">IEC + AI</span>
          </div>
          <div class="kpi-main">
            <span class="kpi-val" [style.color]="getColorHex(data.healthAssessment.color)">
              {{ data.healthAssessment.healthIndex }}<span class="unit">%</span>
            </span>
          </div>
          <div class="kpi-sub">Grade {{ data.healthAssessment.grade || 'N/A' }} Standard</div>
        </div>
  
        <!-- RISK LEVEL -->
        <div class="kpi-box" *ngIf="data.summary" [style.border-bottom-color]="getColorHex(data.summary.color)">
          <div class="kpi-top">
            <span class="kpi-label">RISK STATUS</span>
            <span class="origin-badge ai-bg">AI Inferred</span>
          </div>
          <div class="kpi-main">
            <span class="status-glow-pill" 
                  [style.background-color]="getColorHex(data.summary.color) + '20'" 
                  [style.color]="getColorHex(data.summary.color)"
                  [style.border-color]="getColorHex(data.summary.color)">
              {{ data.summary.riskLevel || 'Unknown' }}
            </span>
          </div>
          <div class="kpi-sub">{{ data.summary.maintenancePriority }} Priority Action</div>
        </div>
  
        <!-- ESTIMATED RUL -->
        <div class="kpi-box" *ngIf="data.remainingUsefulLife">
          <div class="kpi-top">
            <span class="kpi-label">EST. RUL</span>
            <span class="origin-badge ai-bg">AI Prognostic</span>
          </div>
          <div class="kpi-main">
            <span class="kpi-val text-cyan">
              {{ data.remainingUsefulLife.remainingLifeYears }} <span class="unit">Yrs</span>
            </span>
          </div>
          <div class="kpi-sub">Next Inspection: {{ data.remainingUsefulLife.inspection?.days }} Days</div>
        </div>
      </div>
    </header>
  
    <!-- TWO-COLUMN SECTION: DIAGNOSTICS & GLOBAL ALERTS -->
    <div class="diagnostic-grid-container">
  
      <!-- LEFT COLUMN: AI NEURAL DIAGNOSTICS -->
      <div class="grid-column">
        <section class="card glass-card ai-border" *ngIf="data.aiAssessment">
          <div class="card-head">
            <div class="head-title">
              <span class="head-icon">🧠</span>
              <div>
                <h3>AI Neural Diagnostics</h3>
                <span class="head-sub">Anomaly & Behavioral Pattern Detection</span>
              </div>
            </div>
            <span class="head-status-pill" 
                  [style.background-color]="getColorHex(data.aiAssessment.color) + '20'" 
                  [style.color]="getColorHex(data.aiAssessment.color)"
                  [style.border-color]="getColorHex(data.aiAssessment.color)">
              {{ data.aiAssessment.status }}
            </span>
          </div>
  
          <div class="card-body">
            <p class="ai-summary-text">{{ data.aiAssessment.summary }}</p>
  
            <div class="ai-recommendation-callout" *ngIf="data.aiAssessment.recommendation">
              <div class="callout-title">
                <span class="icon">⚡</span>
                <strong>Targeted AI Action Intervention:</strong>
              </div>
              <p class="callout-desc">{{ data.aiAssessment.recommendation }}</p>
            </div>
          </div>
        </section>
      </div>
  
      <!-- RIGHT COLUMN: IEC SUBSYSTEM HEALTH SCORES -->
      <div class="grid-column">
        <section class="card glass-card iec-border" *ngIf="data.healthAssessment?.scoreBreakdown">
          <div class="card-head">
            <div class="head-title">
              <span class="head-icon">📜</span>
              <div>
                <h3>IEC Subsystem Health Scores</h3>
                <span class="head-sub">Deterministic Threshold Standard Analysis</span>
              </div>
            </div>
          </div>
  
          <div class="card-body">
            <div class="score-breakdown-grid">
              <div class="score-row-item" *ngFor="let item of data.healthAssessment.scoreBreakdown | keyvalue">
                <div class="score-meta">
                  <div>
                    <span class="param-title">{{ item.key | titlecase }}</span>
                    <span class="param-std-clause">
                      <ng-container [ngSwitch]="item.key.toLowerCase()">
                        <ng-container *ngSwitchCase="'thermal'">IEC 60076-7</ng-container>
                        <ng-container *ngSwitchCase="'oil'">IEC 60422</ng-container>
                        <ng-container *ngSwitchCase="'electrical'">IEC 60076-1</ng-container>
                        <ng-container *ngSwitchCase="'protection'">IEC 60255</ng-container>
                        <ng-container *ngSwitchDefault>IEC Base Standard</ng-container>
                      </ng-container>
                    </span>
                  </div>
                  <span class="score-percent" [style.color]="getColorHex(item.value >= 90 ? 'lime' : (item.value >= 70 ? 'yellow' : 'red'))">
                    {{ item.value }}%
                  </span>
                </div>
                <div class="bar-track">
                  <div class="bar-fill" 
                       [style.width.%]="item.value" 
                       [style.background-color]="getColorHex(item.value >= 90 ? 'lime' : (item.value >= 70 ? 'yellow' : 'red'))">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
  
    </div>
  
    <!-- IEC / IEEE COMPLIANCE MATRIX -->
    <section class="card glass-card full-span-section" *ngIf="data.iecAssessment">
      <div class="card-head flex-between">
        <div class="head-title">
          <span class="head-icon">🛡️</span>
          <div>
            <h3>IEC / IEEE Standard Compliance Matrix</h3>
            <span class="head-sub">Granular Rule-Based Category Diagnostic & Parameter Violations</span>
          </div>
        </div>
  
        <!-- CATEGORY TABS -->
        <div class="category-tabs">
          <button *ngFor="let cat of iecCategories" 
                  class="tab-pill-btn" 
                  [class.active]="activeCategory === cat.key"
                  (click)="setActiveCategory(cat.key)">
            {{ cat.label }}
            <span class="badge-count" *ngIf="cat.violationsCount > 0">{{ cat.violationsCount }}</span>
          </button>
        </div>
      </div>
  
      <div class="card-body">
        <!-- CATEGORY CARDS GRID -->
        <div class="category-cards-grid">
          <ng-container *ngFor="let cat of iecCategories">
            <div class="cat-card" 
                 *ngIf="cat.key !== 'all' && cat.data && (activeCategory === 'all' || activeCategory === cat.key)"
                 [class.has-fault]="cat.data.violations?.length > 0">
              
              <div class="cat-card-head">
                <div>
                  <span class="cat-name">{{ cat.label }}</span>
                  <span class="cat-std-tag">{{ cat.data.standard || 'IEC Diagnostic' }}</span>
                </div>
                <span class="cat-status-badge" [style.color]="getStatusColor(cat.data.status)">
                  {{ cat.data.status || 'N/A' }}
                </span>
              </div>
  
              <div class="cat-card-metric">
                <span class="m-label">Compliance Index</span>
                <span class="m-value" [style.color]="getStatusColor(cat.data.status)">
                  {{ cat.data.compliance !== undefined ? cat.data.compliance + '%' : 'N/A' }}
                </span>
              </div>
  
              <p class="cat-summary-msg" *ngIf="cat.data.summary?.message">
                {{ cat.data.summary.message }}
              </p>
  
              <!-- PARAMETER VIOLATIONS -->
              <div class="cat-violations-list" *ngIf="cat.data.violations && cat.data.violations.length > 0">
                <div class="cat-v-title">Detected Parameter Faults:</div>
                <div class="cat-v-item" *ngFor="let v of cat.data.violations">
                  <div class="v-header">
                    <span class="v-param-name">{{ v.parameter }}</span>
                    <span class="v-severity-pill" [style.color]="getStatusColor(v.severity)">
                      {{ v.severity }}
                    </span>
                  </div>
                  <div class="v-details">
                    <span>Actual: <strong>{{ v.actualValue || v.value }}{{ v.unit }}</strong></span>
                    <span>Limit: <strong>{{ v.limit }}{{ v.unit }}</strong></span>
                  </div>
                  <p class="v-msg" *ngIf="v.message">{{ v.message }}</p>
                  <div class="v-action" *ngIf="v.recommendedAction">
                    <span>⚡ Action:</span> {{ v.recommendedAction }}
                  </div>
                </div>
              </div>
  
              <!-- NO FAULT INDICATOR -->
              <div class="no-fault-indicator" *ngIf="!cat.data.violations || cat.data.violations.length === 0">
                <span class="check-icon">✓</span> All monitored parameters operating within standard limits.
              </div>
  
            </div>
          </ng-container>
        </div>
  
        <!-- GLOBAL CRITICAL VIOLATIONS ALERT PANEL -->
        <div class="violations-alert-box" *ngIf="data.iecAssessment?.overall?.violations?.length">
          <div class="v-alert-head">
            <span class="v-icon">🚨</span>
            <div>
              <h4>IEC Global Non-Compliance Summary</h4>
              <p class="v-subtext">Cumulative threshold breaches requiring immediate engineering intervention</p>
            </div>
          </div>
          <div class="v-alert-grid">
            <div class="v-alert-card" *ngFor="let v of data.iecAssessment.overall.violations">
              <div class="v-card-top">
                <span class="v-code">{{ v.standard || 'IEC OUT-OF-SPEC' }}</span>
                <span class="v-sev-badge" [style.color]="getStatusColor(v.severity)">{{ v.severity }}</span>
              </div>
              <div class="v-content">
                <strong class="v-param">{{ v.parameter }}</strong>
                <div class="v-metrics-inline">
                  <span>Value: <strong>{{ v.actualValue }}{{ v.unit }}</strong></span>
                  <span>Limit: <strong>{{ v.limit }}{{ v.unit }}</strong></span>
                </div>
                <p class="v-text">{{ v.message }}</p>
                <div class="v-rec" *ngIf="v.recommendedAction">
                  <strong>Recommended Action:</strong> {{ v.recommendedAction }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  
    <!-- ACTIONABLE MAINTENANCE PROTOCOL (POSITIONED AT BOTTOM) -->
    <section class="card glass-card maintenance-protocol-section" *ngIf="data.recommendations && data.recommendations.length">
      <div class="card-head">
        <div class="head-title">
          <span class="head-icon">⚙️</span>
          <div>
            <h3>Actionable Maintenance Protocol</h3>
            <span class="head-sub">Synthesized Operational & Engineering Guidance</span>
          </div>
        </div>
        <span class="protocol-status-badge">Priority Execution</span>
      </div>
  
      <div class="card-body">
        <div class="action-steps-grid">
          <div class="action-step-card" *ngFor="let rec of data.recommendations; let i = index">
            <div class="step-badge">
              <span class="step-number">0{{ i + 1 }}</span>
            </div>
            <div class="step-content">
              <div class="step-header">
                <span class="step-tag">INTERVENTION STEP</span>
                <span class="source-tag">AI Anomaly + IEC Standard Rules</span>
              </div>
              <p class="step-text">{{ rec }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  
  </div>